import {
  BranchCreateRequest,
  BranchResponse,
  BranchUpdateRequest,
} from "../../types/branch.type";

export type BranchFormField = keyof BranchCreateRequest;
export type BranchFormValues = Record<BranchFormField, string>;
export type BranchFormErrors = Partial<Record<BranchFormField, string>>;

/** NN and AA make up the branch code, so they are set once, on create. */
const IMMUTABLE_FIELDS = ["branch_number", "branch_prefix"] as const;
const REQUIRED_EDITABLE_FIELDS = [
  "branch_name",
  "address",
  "subdistrict",
  "district",
  "province",
  "zipcode",
  "phone",
  "express_doc_40",
  "express_doc_42",
] as const;
const OPTIONAL_FIELDS = ["company_email", "company_taxid"] as const;

/** Every field of the create form, in the order the form renders them. */
export const CREATE_FIELDS: readonly BranchFormField[] = [
  ...IMMUTABLE_FIELDS,
  ...REQUIRED_EDITABLE_FIELDS,
  ...OPTIONAL_FIELDS,
];
export const EDIT_FIELDS: readonly BranchFormField[] = [
  ...REQUIRED_EDITABLE_FIELDS,
  ...OPTIONAL_FIELDS,
];
export const LOCKED_EDIT_FIELDS: readonly BranchFormField[] = IMMUTABLE_FIELDS;

const OPTIONAL_FIELD_SET: ReadonlySet<BranchFormField> = new Set(OPTIONAL_FIELDS);

export const isOptionalField = (field: BranchFormField) =>
  OPTIONAL_FIELD_SET.has(field);

/** Grid columns out of 12 on a wide screen; every field is full width on a phone. */
type GridSize = 3 | 4 | 6 | 12;

interface BranchFieldConfig {
  label: string;
  placeholder?: string;
  maxLength?: number;
  /** The format the backend validator enforces; checked on the trimmed value. */
  pattern?: RegExp;
  patternMessage?: string;
  /** Codes the backend only accepts in capitals, so typing is capitalised as it happens. */
  uppercase?: boolean;
  gridSize: GridSize;
}

const EXPRESS_DOC_PATTERN = /^[A-Z0-9]{2}$/;
const EXPRESS_DOC_MESSAGE = "ต้องเป็นตัวอักษรภาษาอังกฤษหรือตัวเลข 2 ตัว";
const EXPRESS_DOC_LENGTH = 2;
const PLACE_NAME_MAX_LENGTH = 200;

export const BRANCH_FIELD_CONFIG: Record<BranchFormField, BranchFieldConfig> = {
  branch_number: {
    label: "เลขสาขา (NN)",
    placeholder: "เช่น 34",
    maxLength: 2,
    pattern: /^\d{2}$/,
    patternMessage: "ต้องเป็นตัวเลข 2 หลัก",
    gridSize: 3,
  },
  branch_prefix: {
    label: "ตัวย่อ (AA)",
    placeholder: "เช่น TK",
    maxLength: 2,
    pattern: /^[A-Z]{2}$/,
    patternMessage: "ต้องเป็นตัวอักษรภาษาอังกฤษ 2 ตัว",
    uppercase: true,
    gridSize: 3,
  },
  branch_name: { label: "ชื่อสาขา", maxLength: 255, gridSize: 6 },
  address: { label: "ที่อยู่", gridSize: 12 },
  subdistrict: { label: "ตำบล", maxLength: PLACE_NAME_MAX_LENGTH, gridSize: 4 },
  district: { label: "อำเภอ", maxLength: PLACE_NAME_MAX_LENGTH, gridSize: 4 },
  province: { label: "จังหวัด", maxLength: PLACE_NAME_MAX_LENGTH, gridSize: 4 },
  zipcode: {
    label: "รหัสไปรษณีย์",
    maxLength: 5,
    pattern: /^\d{5}$/,
    patternMessage: "ต้องเป็นตัวเลข 5 หลัก",
    gridSize: 4,
  },
  phone: {
    label: "เบอร์โทร",
    placeholder: "เช่น 0812345678",
    maxLength: 10,
    pattern: /^(08|09|06)\d{8}$|^(02|03|04|05|07)\d{7}$/,
    patternMessage: "ตัวเลขเท่านั้น มือถือ 10 หลัก (06/08/09) หรือโทรศัพท์บ้าน 9 หลัก",
    gridSize: 4,
  },
  express_doc_40: {
    label: "รหัสเอกสาร Express 40",
    placeholder: "เช่น U2",
    maxLength: EXPRESS_DOC_LENGTH,
    pattern: EXPRESS_DOC_PATTERN,
    patternMessage: EXPRESS_DOC_MESSAGE,
    uppercase: true,
    gridSize: 4,
  },
  express_doc_42: {
    label: "รหัสเอกสาร Express 42",
    placeholder: "เช่น Z2",
    maxLength: EXPRESS_DOC_LENGTH,
    pattern: EXPRESS_DOC_PATTERN,
    patternMessage: EXPRESS_DOC_MESSAGE,
    uppercase: true,
    gridSize: 4,
  },
  company_email: {
    label: "อีเมล",
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    patternMessage: "รูปแบบอีเมลไม่ถูกต้อง",
    gridSize: 4,
  },
  company_taxid: {
    label: "เลขประจำตัวผู้เสียภาษี",
    maxLength: 13,
    pattern: /^\d{13}$/,
    patternMessage: "ต้องเป็นตัวเลข 13 หลัก",
    gridSize: 4,
  },
};

export const EMPTY_BRANCH_FORM: BranchFormValues = {
  branch_number: "",
  branch_prefix: "",
  branch_name: "",
  address: "",
  subdistrict: "",
  district: "",
  province: "",
  zipcode: "",
  phone: "",
  express_doc_40: "",
  express_doc_42: "",
  company_email: "",
  company_taxid: "",
};

export const isBranchFormField = (name: string): name is BranchFormField =>
  Object.prototype.hasOwnProperty.call(BRANCH_FIELD_CONFIG, name);

export const normalizeFieldInput = (field: BranchFormField, value: string) =>
  BRANCH_FIELD_CONFIG[field].uppercase ? value.toUpperCase() : value;

const validateField = (field: BranchFormField, value: string) => {
  const config = BRANCH_FIELD_CONFIG[field];
  if (!value) {
    return isOptionalField(field) ? undefined : `กรุณากรอก${config.label}`;
  }
  if (config.maxLength !== undefined && value.length > config.maxLength) {
    return `ไม่เกิน ${config.maxLength} ตัวอักษร`;
  }
  if (config.pattern && !config.pattern.test(value)) {
    return config.patternMessage;
  }
  return undefined;
};

/** Field-level Thai messages for `fields`; an empty object means the form may be sent. */
export const validateBranchForm = (
  values: BranchFormValues,
  fields: readonly BranchFormField[],
): BranchFormErrors => {
  const errors: BranchFormErrors = {};
  fields.forEach((field) => {
    const message = validateField(field, values[field].trim());
    if (message) errors[field] = message;
  });
  return errors;
};

/** Legacy branches carry nulls in fields the form now requires; they open as empty inputs. */
export const mapBranchToForm = (branch: BranchResponse): BranchFormValues => ({
  branch_number: branch.branch_number ?? "",
  branch_prefix: branch.branch_prefix ?? "",
  branch_name: branch.branch_name,
  address: branch.address ?? "",
  subdistrict: branch.subdistrict ?? "",
  district: branch.district ?? "",
  province: branch.province ?? "",
  zipcode: branch.zipcode ?? "",
  phone: branch.phone ?? "",
  express_doc_40: branch.express_doc_40 ?? "",
  express_doc_42: branch.express_doc_42 ?? "",
  company_email: branch.company_email ?? "",
  company_taxid: branch.company_taxid ?? "",
});

const toOptionalValue = (value: string) => value.trim() || null;

export const buildBranchCreatePayload = (
  values: BranchFormValues,
): BranchCreateRequest => ({
  branch_number: values.branch_number.trim(),
  branch_prefix: values.branch_prefix.trim(),
  branch_name: values.branch_name.trim(),
  address: values.address.trim(),
  subdistrict: values.subdistrict.trim(),
  district: values.district.trim(),
  province: values.province.trim(),
  zipcode: values.zipcode.trim(),
  phone: values.phone.trim(),
  express_doc_40: values.express_doc_40.trim(),
  express_doc_42: values.express_doc_42.trim(),
  company_email: toOptionalValue(values.company_email),
  company_taxid: toOptionalValue(values.company_taxid),
});

/**
 * Only the fields that differ from what the branch holds. A cleared optional field is sent
 * as `null` so the server clears it too; required fields are never cleared (validation
 * refuses an empty one first).
 */
export const buildBranchUpdatePayload = (
  initial: BranchFormValues,
  current: BranchFormValues,
): BranchUpdateRequest => {
  const payload: BranchUpdateRequest = {};
  REQUIRED_EDITABLE_FIELDS.forEach((field) => {
    const value = current[field].trim();
    if (value !== initial[field].trim()) payload[field] = value;
  });
  OPTIONAL_FIELDS.forEach((field) => {
    const value = current[field].trim();
    if (value !== initial[field].trim()) payload[field] = toOptionalValue(value);
  });
  return payload;
};

const BRANCH_NUMBER_PLACEHOLDER = "NN";
const BRANCH_PREFIX_PLACEHOLDER = "AA";
/** Every branch gets route `<AA>99`, its SPECIAL (counter-pickup) route. */
const SPECIAL_ROUTE_NUMBER = "99";

/** The `NN-AA` code the server will compose, with placeholders for what is not typed yet. */
export const composeBranchCode = (branchNumber: string, branchPrefix: string) =>
  `${branchNumber.trim() || BRANCH_NUMBER_PLACEHOLDER}-${
    branchPrefix.trim() || BRANCH_PREFIX_PLACEHOLDER
  }`;

export const composeSpecialRouteCode = (branchPrefix: string) =>
  `${branchPrefix.trim() || BRANCH_PREFIX_PLACEHOLDER}${SPECIAL_ROUTE_NUMBER}`;
